import type { ReactNode } from 'react';
import { Navigate, Route } from 'react-router-dom';

import { CustomerProtectedRoute } from '@/components/auth/CustomerProtectedRoute';
import { CustomerLayout } from '@/components/layouts/customer-layout';
import CustomerSettingsPage from '@/pages/customer/settings';
import CustomerTeamsPage from '@/pages/customer/teams';
import CustomerDashboard from '@/pages/customer/dashboard';
import CustomerLogin from '@/pages/customer/login';
import CustomerVerify from '@/pages/customer/verify';
import CustomerForgotPassword from '@/pages/customer/forgot-password';
import CustomerResetPassword from '@/pages/customer/reset-password';
import CustomerSsoConsumer from '@/pages/customer/sso';
import { CustomerSsoGuard } from '@/components/customer/CustomerSsoGuard';
import CustomerBillingPage from '@/pages/customer/billing';
import CustomerCreditsPage from '@/pages/customer/credits';
import CustomerPlanManagementCheckoutPage from '@/pages/customer/plan-management-checkout';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { useCustomerTeams } from '@/hooks/customer/useCustomerTeams';

// eslint-disable-next-line react-refresh/only-export-components
function CustomerProtectedLayout() {
  return (
    <CustomerProtectedRoute>
      <CustomerLayout />
    </CustomerProtectedRoute>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function CustomerTeamsAccessRoute() {
  const { hasRole, isLoading } = useCustomerAuth();
  const teamsQuery = useCustomerTeams();

  if (isLoading || teamsQuery.isLoading) {
    return null;
  }

  const hasTeamRoleFromMembership = (teamsQuery.data ?? []).some(
    (team) => team.role === 'owner' || team.role === 'admin',
  );

  const hasTeamRoleByAuth = hasRole([
    'customer_owner',
    'customer_admin',
    'team_owner',
    'team_admin',
  ]);

  const isIndividualWithoutTeamMembership =
    hasRole(['customer_individual', 'individual']) && !hasTeamRoleFromMembership;

  if (isIndividualWithoutTeamMembership || (!hasTeamRoleByAuth && !hasTeamRoleFromMembership)) {
    return <Navigate to="/customer" replace />;
  }

  return <CustomerTeamsPage />;
}

// Billing should be available to individual customers and team owners.
// eslint-disable-next-line react-refresh/only-export-components
function CustomerBillingAccessRoute({ children }: { children: ReactNode }) {
  const { hasRole, isLoading, user } = useCustomerAuth();
  const teamsQuery = useCustomerTeams();

  if (isLoading || teamsQuery.isLoading) {
    return null;
  }

  const hasTeamOwnerMembership = (teamsQuery.data ?? []).some((team) => team.role === 'owner');
  const hasNoTeamMembership = (teamsQuery.data ?? []).length === 0;
  const hasUnspecifiedCustomerRole = !user?.role && (user?.roles?.length ?? 0) === 0;
  const hasBillingAccessByRole = hasRole(['owner', 'customer_owner', 'customer_individual', 'individual']);

  if (
    !hasBillingAccessByRole &&
    !hasTeamOwnerMembership &&
    !(hasUnspecifiedCustomerRole && hasNoTeamMembership)
  ) {
    return <Navigate to="/customer" replace />;
  }

  return <>{children}</>;
}

export const customerRoutes = (
  <>
    <Route
      path="/customer/login"
      element={
        <CustomerSsoGuard>
          <CustomerLogin />
        </CustomerSsoGuard>
      }
    />
    <Route path="/customer/forgot-password" element={<CustomerForgotPassword />} />
    <Route path="/customer/reset-password" element={<CustomerResetPassword />} />
    <Route path="/customer/login/verify" element={<CustomerVerify />} />
    <Route path="/customer/sso" element={<CustomerSsoConsumer />} />
    <Route path="/customer" element={<CustomerProtectedLayout />}>
      <Route index element={<CustomerDashboard />} />
      <Route
        path="billing"
        element={
          <CustomerBillingAccessRoute>
            <CustomerBillingPage />
          </CustomerBillingAccessRoute>
        }
      />
      <Route
        path="billing/checkout"
        element={
          <CustomerBillingAccessRoute>
            <CustomerPlanManagementCheckoutPage />
          </CustomerBillingAccessRoute>
        }
      />
      <Route
        path="credits_preview"
        element={<CustomerCreditsPage />}
      />
      <Route
        path="subscriptions"
        element={
          <CustomerBillingAccessRoute>
            <Navigate to="/customer/billing?tab=subscriptions" replace />
          </CustomerBillingAccessRoute>
        }
      />
      <Route
        path="teams"
        element={<CustomerTeamsAccessRoute />}
      />
      <Route path="teams/create" element={<Navigate to="/customer/teams" replace />} />
      <Route
        path="settings"
        element={<CustomerSettingsPage />}
      />
    </Route>
  </>
);
